import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { APIProvider, Map, AdvancedMarker, useMapsLibrary, useMap } from '@vis.gl/react-google-maps'
import Loading from '../common/Loading'
import { Box } from '@mui/material'

function Directions({ loc, dest, setDistance }) {
  const map = useMap()
  const routesLibrary = useMapsLibrary('routes')
  const [directionsService, setDirectionsService] = useState(null)
  const [directionsRenderer, setDirectionsRenderer] = useState(null)

  useEffect(() => {
    if (!routesLibrary || !map) return
    const service = new routesLibrary.DirectionsService()
    const renderer = new routesLibrary.DirectionsRenderer({ map })
    setDirectionsService(service)
    setDirectionsRenderer(renderer)
    return () => renderer.setMap(null)
  }, [routesLibrary, map])

  useEffect(() => {
    if (!directionsService || !directionsRenderer || !loc || !dest) return

    directionsService
      .route({
        origin: loc,
        destination: dest,
        travelMode: 'DRIVING',
        provideRouteAlternatives: false
      })
      .then((response) => {
        directionsRenderer.setDirections(response)
        if (response.routes?.[0]?.legs?.[0]) {
          setDistance({
            distance: response.routes[0].legs[0].distance,
            duration: response.routes[0].legs[0].duration
          })
        }
      })
      .catch()
  }, [directionsService, directionsRenderer, loc, dest, setDistance])

  return null
}

function MapGoogle({ customer, company, setDistance }) {
  const [loc, setLoc] = useState(null)
  const [dest, setDest] = useState(null)
  const [loading, setLoading] = useState(true)

  const companyAddress = useMemo(() => {
    if (!company) return null
    return `${company.office_address1}, ${company.office_zip}, ${company.office_city}`
  }, [company])

  const customerAddress = useMemo(() => {
    if (!customer) return null
    return `${customer.address1}, ${customer.zip}, ${customer.city}`
  }, [customer])

  const geocodeAddress = useCallback(async (address) => {
    if (!address) return null
    const key = process.env.REACT_APP_GOOGLE_API_KEY
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${key}`
    try {
      const res = await fetch(url)
      const data = await res.json()
      if (data.status === 'OK' && data.results.length > 0) {
        return data.results[0].geometry.location
      }
      return null
    } catch (error) {
      return null
    }
  }, [])

  useEffect(() => {
    if (!companyAddress || !customerAddress) return

    setLoading(true)
    Promise.all([geocodeAddress(companyAddress), geocodeAddress(customerAddress)])
      .then(([locRes, destRes]) => {
        setLoc(locRes)
        setDest(destRes)
      })
      .finally(() => setLoading(false))
  }, [companyAddress, customerAddress, geocodeAddress])

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          height: '100%',
          minHeight: '100px',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loading />
      </Box>
    )
  if (!loc || !dest) return null

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_API_KEY}>
      <Map style={{ width: 300, height: 300 }} defaultCenter={loc} defaultZoom={12} mapId='DEMO_MAP_ID'>
        <AdvancedMarker position={loc} />
        <AdvancedMarker position={dest} />
        <Directions loc={loc} dest={dest} setDistance={setDistance} />
      </Map>
    </APIProvider>
  )
}

export default MapGoogle
